import { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { X, Copy, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { AuthContext } from '@/App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PromoPopup() {
  const { user } = useContext(AuthContext);
  const [popup, setPopup] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Don't show popups to wholesale or staff users
    if (user && (user.role === 'wholesale' || user.role === 'staff')) {
      return;
    }
    
    // Only show popups on true "shopping" surfaces (home, products, PDP).
    // Skip auth, cart/checkout, staff, wholesale, and all static/info pages
    // (about, contact, locations, coas, policies, etc.) where the
    // modal would obscure content the user came to read.
    const path = window.location.pathname;
    const isShoppingSurface =
      path === '/' ||
      path === '/products' ||
      path.startsWith('/products/');
    if (!isShoppingSurface) {
      return;
    }
    
    checkForActivePopup();
  }, [user]);

  const checkForActivePopup = async () => {
    try {
      // Check if user has dismissed this popup in this session
      const dismissedPopups = JSON.parse(sessionStorage.getItem('dismissedPopups') || '[]');
      
      const response = await axios.get(`${BACKEND_URL}/api/popups/active`);
      
      if (response.data && !dismissedPopups.includes(response.data.id)) {
        setPopup(response.data);
        // Small delay before showing for better UX
        setTimeout(() => setIsVisible(true), 1500);
      }
    } catch (error) {
      // No active popup or error - silently fail
      console.log('No active popup');
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    
    // Remember that user dismissed this popup for this session
    if (popup) {
      const dismissedPopups = JSON.parse(sessionStorage.getItem('dismissedPopups') || '[]');
      dismissedPopups.push(popup.id);
      sessionStorage.setItem('dismissedPopups', JSON.stringify(dismissedPopups));
    }
  };

  const handleCopyCode = () => {
    if (popup?.discountCode) {
      navigator.clipboard.writeText(popup.discountCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!popup) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
          onClick={handleClose}
          data-testid="promo-popup-overlay"
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 50 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative rounded-xl p-8 md:p-10 text-center max-w-lg w-full shadow-2xl"
            style={{ 
              backgroundColor: popup.backgroundColor || '#000000',
              borderWidth: '3px',
              borderColor: popup.accentColor || '#C1FF00'
            }}
            onClick={(e) => e.stopPropagation()}
            data-testid="promo-popup-content"
          >
            {/* Close Button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 transition-colors"
              style={{ color: popup.textColor || '#FFFFFF' }}
              data-testid="promo-popup-close"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Title */}
            <motion.h2
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="font-anton text-3xl md:text-4xl uppercase mb-4 leading-tight"
              style={{ color: popup.accentColor || '#C1FF00' }}
            >
              {popup.title}
            </motion.h2>

            {/* Content */}
            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-lg md:text-xl mb-6 leading-relaxed"
              style={{ color: popup.textColor || '#FFFFFF' }}
            >
              {popup.content}
            </motion.p>

            {/* Discount Code */}
            {popup.discountCode && (
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="mb-6"
              >
                <p 
                  className="text-sm uppercase tracking-wider mb-2"
                  style={{ color: popup.textColor || '#FFFFFF', opacity: 0.7 }}
                >
                  Use Code:
                </p>
                <button
                  onClick={handleCopyCode}
                  className="inline-flex items-center gap-3 px-6 py-3 rounded-lg font-mono text-xl md:text-2xl font-bold transition-all hover:scale-105"
                  style={{ 
                    backgroundColor: popup.accentColor + '20',
                    color: popup.accentColor,
                    borderWidth: '2px',
                    borderStyle: 'dashed',
                    borderColor: popup.accentColor
                  }}
                  data-testid="promo-popup-code"
                >
                  {popup.discountCode}
                  {copied ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <Copy className="w-5 h-5" />
                  )}
                </button>
                {copied && (
                  <p 
                    className="text-sm mt-2"
                    style={{ color: popup.accentColor }}
                  >
                    Copied to clipboard!
                  </p>
                )}
              </motion.div>
            )}

            {/* CTA Button */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <Link
                to={popup.ctaUrl || '/products'}
                onClick={handleClose}
                className="inline-block px-10 py-4 rounded-lg font-anton uppercase text-lg tracking-wider transition-all hover:scale-105 hover:shadow-lg"
                style={{ 
                  backgroundColor: popup.accentColor || '#C1FF00',
                  color: popup.backgroundColor || '#000000'
                }}
                data-testid="promo-popup-cta"
              >
                {popup.ctaText || 'Shop Now'}
              </Link>
            </motion.div>

            {/* Decorative Elements */}
            <div 
              className="absolute top-0 left-0 w-20 h-20 opacity-20 -translate-x-1/2 -translate-y-1/2 rounded-full blur-xl"
              style={{ backgroundColor: popup.accentColor }}
            />
            <div 
              className="absolute bottom-0 right-0 w-32 h-32 opacity-20 translate-x-1/2 translate-y-1/2 rounded-full blur-xl"
              style={{ backgroundColor: popup.accentColor }}
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
